// Opponent seat — avatar with timer ring + name/wallet pill + strike dots.
// Layout position (top / left-top / left-bot / right-top / right-bot)
// is decided by Table.tsx based on player count.

type Profile = {
  displayName?: string;
  avatarUrl?: string | null;
  wallet?: number;
  isBot?: boolean;
  /** Bot difficulty (`bots.personality_type` from DB, or the bracket
   *  fallback for synthetic bots). Rendered as a colour-coded
   *  EASY/MEDIUM/HARD tag next to the name when isBot is true.
   *  Mirrors the Carrom in-match HUD treatment. null for humans. */
  botMode?: string | null;
};

type Props = {
  seat: string;
  profile?: Profile;
  side: 'top' | 'left' | 'right';
  isTurn: boolean;
  absent?: boolean;
  out?: boolean;          // kicked / dropped
  // Consecutive turn-timeouts (rulebook 6.8). 0..strikeLimit (default 3).
  // Shown as coloured dots so the player actually sees the counter
  // climbing — otherwise a silent auto-kick feels random.
  strikes?: number;
  strikeLimit?: number;
  // Bump this on every turn rotation so the ring SVG remounts and the
  // drain animation restarts cleanly. Without it the CSS animation
  // only plays once per component lifetime.
  turnSeq?: number;
  onEmoji?: () => void;
  position: { top?: string; bottom?: string; left?: string; right?: string };
};

export default function Seat({
  seat, profile, side, isTurn, absent, out, strikes = 0, strikeLimit = 3,
  turnSeq = 0, onEmoji, position,
}: Props) {
  const name = profile?.displayName || seat;
  const wallet = profile?.wallet ?? 0;
  const src = profile?.avatarUrl || '';
  const dots = Math.max(0, Math.min(strikeLimit, strikes | 0));
  return (
    <div
      className={[
        'rummy-seat',
        side === 'right' ? 'rummy-seat--right' : '',
        isTurn && !absent && !out ? 'rummy-seat--turn' : '',
        absent ? 'rummy-seat--absent' : '',
        out    ? 'rummy-seat--out' : '',
      ].filter(Boolean).join(' ')}
      style={position}
      onClick={onEmoji}
    >
      <div className="rummy-seat__avatar">
        <svg
          key={`seat-ring-${seat}-${turnSeq}`}
          viewBox="0 0 56 56"
          className={`rummy-seat__ring rummy-timer-ring ${isTurn && !absent && !out ? 'rummy-timer-ring--active' : ''}`}
        >
          <circle cx="28" cy="28" r="26" />
        </svg>
        {src
          ? <img className="rummy-seat__img" src={src} alt="" />
          : <div className="rummy-seat__img" style={{ background: '#22543d', display: 'grid', placeItems: 'center', fontWeight: 900, color: '#fde08b' }}>{name.slice(0, 1).toUpperCase()}</div>}
      </div>
      <div className="rummy-seat__label">
        <div className="rummy-seat__name">
          {/* Bot difficulty pill removed — players must never see
              any indicator that they're playing against a bot.
              `profile.botMode` is still accepted in the type so the
              engine state can drive non-visible behaviour, but
              produces no visible output here. */}
          {out ? 'Left' : absent ? 'Away…' : name}
        </div>
        {!out && <div className="rummy-seat__wallet">{wallet.toLocaleString('en-IN')}</div>}
        {!out && !absent && strikeLimit > 0 && (
          <div className="rummy-seat__strikes" aria-label={`${dots} of ${strikeLimit} strikes`}>
            {Array.from({ length: strikeLimit }).map((_, i) => (
              <span
                key={i}
                className={`rummy-seat__strike ${i < dots ? 'rummy-seat__strike--on' : ''}`}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
